import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const AdminSubmissionList = () => {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const location = useLocation();
  const defaultFilter = location.state?.defaultFilter || 'all';
  const [filter, setFilter] = useState(defaultFilter);
  const [page, setPage] = useState(1);
  const perPage = 10;
  const API = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const res = await axios.get(`${API}/api/admin/submissions`);
        setSubmissions(res.data);
        setPage(1);
      } catch (err) {
        console.error('Error fetching submissions:', err);
      }
    };
    fetchSubmissions();
  }, [API]);

  useEffect(() => {
    if (filter === 'all') {
      const allSubs = submissions.map(sub =>
        sub.Status === 'Pending' && sub.IsReview === 'Reviewed'
          ? { ...sub, Status: 'Accept' }
          : sub
      );
      setFilteredSubmissions(allSubs);
    } else if (filter === 'accepted') {
      setFilteredSubmissions(
        submissions.filter(sub => sub.Status === 'Accept' && sub.parentSubmissionID != null)
      );
    } else {
      const grouped = submissions.reduce((acc, sub) => {
        const key = sub.parentSubmissionID ?? sub.submissionID;
        (acc[key] ||= []).push(sub);
        return acc;
      }, {});
      const result = Object.values(grouped).flatMap(group => {
        const hasRevision = group.some(s => /_Revision$/i.test(s.Title));
        if (hasRevision) {
          return group.filter(s => /_Revision$/i.test(s.Title) && s.Status === 'Pending');
        }
        const hasFull = group.some(s => /_Full_Paper$/i.test(s.Title));
        if (hasFull) {
          return group.filter(s => /_Full_Paper$/i.test(s.Title) && s.Status === 'Pending');
        }
        return group.filter(s => s.Status === 'Pending');
      });
      setFilteredSubmissions(result);
    }
  }, [submissions, filter]);

  const paginatedSubmissions = filteredSubmissions.slice((page - 1) * perPage, page * perPage);

  // PDF export function
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Admin Submissions Report', 14, 22);
    autoTable(doc, {
      startY: 30,
      head: [['Title', 'Author', 'Status', 'Date']],
      body: filteredSubmissions.map(sub => [
        sub.Title,
        sub.AuthorName || 'N/A',
        sub.Status,
        new Date(sub.CreatedAt).toLocaleDateString(),
      ]),
      styles: { fontSize: 10, cellPadding: 2 },
      theme: 'grid',
    });
    doc.save('admin_submissions_report.pdf');
  };

  // Excel export function
  const exportToExcel = () => {
    const data = filteredSubmissions.map(sub => ({
      Title: sub.Title,
      Author: sub.AuthorName || 'N/A',
      Status: sub.Status,
      Date: new Date(sub.CreatedAt).toLocaleDateString(),
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Submissions');
    XLSX.writeFile(workbook, 'admin_submissions.xlsx');
  };

  return (
    <div className="container mt-4">
      <h3>Paper Submissions</h3>

      {/* Filter Radio Buttons */}
      <div className="mb-3">
        <label className="me-3">
          <input
            type="radio"
            name="status"
            value="all"
            checked={filter === 'all'}
            onChange={() => setFilter('all')}
          />
          {' '}All Submissions
        </label>
        <label className="me-3">
          <input
            type="radio"
            name="status"
            value="accepted"
            checked={filter === 'accepted'}
            onChange={() => setFilter('accepted')}
          />
          {' '}Accepted Submissions
        </label>
        <label>
          <input
            type="radio"
            name="status"
            value="pending"
            checked={filter === 'pending'}
            onChange={() => setFilter('pending')}
          />
          {' '}Pending Submissions
        </label>
      </div>

      {/* Submissions Table */}
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Title</th>
            <th>Author</th>
            <th>Status</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {paginatedSubmissions.length === 0 ? (
            <tr>
              <td colSpan={4} className="text-center">No submissions found.</td>
            </tr>
          ) : (
            paginatedSubmissions.map(sub => (
              <tr key={sub.submissionID}>
                <td>{sub.Title}</td>
                <td>{sub.AuthorName || 'N/A'}</td>
                <td>{sub.Status}</td>
                <td>{new Date(sub.CreatedAt).toLocaleDateString()}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div className="d-flex justify-content-between mt-3">
        <button
          className="btn btn-secondary"
          onClick={() => setPage(prev => Math.max(prev - 1, 1))}
          disabled={page === 1}
        >
          Previous
        </button>
        <span>Page {page}</span>
        <button
          className="btn btn-secondary"
          onClick={() => setPage(prev => prev + 1)}
          disabled={page * perPage >= filteredSubmissions.length}
        >
          Next
        </button>
      </div>

      {/* Export Buttons */}
      {filteredSubmissions.length > 0 && (
        <div className="mt-3">
          <button className="btn btn-success me-2" onClick={exportToPDF}>
            Export to PDF
          </button>
          <button className="btn btn-outline-primary" onClick={exportToExcel}>
            Export to Excel
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminSubmissionList;
